<template>
  <div class="kiosk-step-payment">
    <!-- Success View (Order Confirmed) -->
    <div
      v-if="confirmedOrder"
      class="payment-success-screen"
    >
      <div class="success-content fade-in">
        <div class="success-icon-wrapper">
          <div class="success-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
        </div>
        <h2 class="success-title">
          Order Confirmed!
        </h2>
        <div class="success-info-card">
          <div class="success-info-item">
            <p class="success-info-label">
              Pickup Time
            </p>
            <p class="success-info-value">
              {{ pickupTimeText }}
            </p>
          </div>
          <div class="success-info-item">
            <p class="success-info-label">
              Customer
            </p>
            <p class="success-info-value">
              {{ customerDisplay }}
            </p>
          </div>
        </div>
        <p class="success-order-number">
          Order #{{ orderId }}
        </p>
        <p class="success-receipt-text">
          Please take your receipt.
        </p>
      </div>
    </div>

    <!-- Processing View -->
    <div
      v-else-if="isProcessing"
      class="payment-processing-screen"
    >
      <div class="processing-content fade-in">
        <div class="processing-spinner">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#F59E0B"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="spinner-icon"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </div>
        <h2 class="processing-title">
          Processing Payment...
        </h2>
      </div>
    </div>

    <!-- Payment Screen (Initial) -->
    <div
      v-else
      class="payment-container"
    >
      <!-- Payment Screen with Dark Background -->
      <div class="payment-screen">
        <!-- Back and Cancel Buttons -->
        <button
          class="payment-back-btn"
          @click.stop="$emit('back')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
        </button>

        <button
          class="payment-cancel-btn"
          @click.stop="handleCancel"
        >
          Cancel Order
        </button>

        <!-- Main Content -->
        <div class="payment-content">
          <!-- Order Total Summary Component -->
          <OrderTotalSummary
            :total="total"
            :subtotal="subtotal"
            :tax-total="taxTotal"
            :currency="currency"
            :surcharge-value="effectiveSurcharge"
            :payment-types="paymentTypes"
            :scheduling-text="schedulingText"
            :customer-name="customerDisplay"
            class="payment-order-summary"
          />

          <!-- Payment Methods Selection (one or more - always selectable) -->
          <div
            v-if="paymentTypes.length > 0"
            class="payment-methods-section"
          >
            <h3 class="payment-methods-title">
              Select Payment Type
            </h3>
            <div class="payment-methods-grid">
              <button
                v-for="method in paymentTypes"
                :key="method.id"
                class="payment-method-btn"
                :class="{ disabled: isProcessing }"
                @click.stop="!isProcessing && selectPayment(method)"
              >
                <div class="method-icon">
                  <i
                    v-if="isCash(method)"
                    class="fa fa-money-bill-wave"
                  />
                  <i
                    v-else
                    class="fa fa-credit-card"
                  />
                </div>
                <div class="payment-name">
                  {{ method.name }}
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Background Decoration -->
        <div class="payment-bg-decoration" />
      </div>
    </div>
  </div>
</template>

<script>
import OrderTotalSummary from "../components/OrderTotalSummary.vue";

export default {
  name: "StepPayment",
  components: {
    OrderTotalSummary,
  },
  props: {
    total: {
      type: Number,
      default: 0,
    },
    subtotal: {
      type: Number,
      default: null,
    },
    taxTotal: {
      type: Number,
      default: null,
    },
    currency: {
      type: String,
      default: "$",
    },
    schedulingText: {
      type: String,
      default: "Preparing Immediately",
    },
    customer: {
      type: Object,
      default: () => ({ name: "Guest", phone: "" }),
    },
    paymentTypes: {
      type: Array,
      default: () => [],
    },
    isProcessing: {
      type: Boolean,
      default: false,
    },
    confirmedOrder: {
      type: Object,
      default: null,
    },
  },
  computed: {
    customerDisplay() {
      if (this.confirmedOrder && this.confirmedOrder.customer) {
        return this.confirmedOrder.customer.first_name || "Guest";
      }
      return this.customer.name || "Guest";
    },
    store() {
      return this.$store.getters["config/store"];
    },
    terminal() {
      return this.$store.getters["config/terminal"];
    },
    effectiveSurcharge() {
      // Rule: If single payment method has surcharge, or terminal has global surcharge_value
      if (
        this.paymentTypes.length === 1 &&
        this.paymentTypes[0].surcharge > 0
      ) {
        return this.paymentTypes[0].surcharge;
      }

      // Check for terminal level surcharge if it exists (fallback)
      if (this.terminal && this.terminal.surcharge_value > 0) {
        return this.terminal.surcharge_value;
      }

      return 0;
    },
    orderId() {
      if (!this.confirmedOrder) return "---";
      return this.confirmedOrder.id || this.confirmedOrder.local_id || "---";
    },
    pickupTimeText() {
      if (!this.confirmedOrder) return this.schedulingText;
      if (!this.confirmedOrder.serve_at) return "ASAP";
      const moment = require("moment");
      return moment(this.confirmedOrder.serve_at).format("h:mm A");
    },
  },
  methods: {
    selectPayment(method) {
      this.$emit("initiate-payment", method);
    },
    isCash(method) {
      return (
        method.behavior === 1 || method.name.toLowerCase().includes("cash")
      );
    },
    handleCancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<style scoped>
.kiosk-step-payment {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  color: #2c3e50;
  position: relative;
}

.payment-processing-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, #1976d2 0%, #2196f3 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  color: white;
  overflow: hidden;
}

.kiosk-bg {
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 1;
}

.kiosk-logo {
  max-height: 15%;
  position: absolute;
  z-index: 7;
  left: 50%;
  transform: translateX(-50%);
  top: 40px;
}

.payment-processing-overlay::after {
  content: "";
  position: absolute;
  z-index: 3;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
}

.processing-content {
  position: relative;
  z-index: 10;
  text-align: center;
}

.spinner-wrapper {
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
}

.payment-loader {
  width: 90px;
  height: 90px;
  border: 8px solid rgba(255, 255, 255, 0.1);
  border-left-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.processing-text {
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: 0.5px;
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.payment-container {
  height: 100%;
  width: 100%;
  position: relative;
}

.payment-screen {
  height: 100%;
  width: 100%;
  background: #0f172a;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 7rem 2rem 3rem;
  text-align: center;
  position: relative;
  overflow-y: auto;
  scrollbar-width: none; /* Hide scrollbar for Firefox */
}

.payment-screen::-webkit-scrollbar {
  display: none; /* Hide scrollbar for Chrome/Safari */
}

.payment-back-btn {
  position: absolute;
  top: 2rem;
  left: 1.5rem;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(0.95);
}

.payment-cancel-btn {
  position: absolute;
  top: 2rem;
  right: 1.5rem;
  padding: 0.5rem 1rem;
  background: #fef2f2;
  border-radius: 0.5rem;
  border: none;
  color: #ef4444;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.payment-cancel-btn:hover {
  background: #fee2e2;
  transform: scale(1.02);
}

.payment-content {
  position: relative;
  max-width: 600px;
  z-index: 10;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 0 1rem;
  margin: auto 0;
}

.payment-order-summary {
  width: 100%;
  margin-bottom: 1rem;
}

.payment-order-summary :deep(.order-total-summary) {
  margin-bottom: 0;
}

.payment-methods-section {
  width: 100%;
}

.payment-methods-title {
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 1rem 0;
  text-align: center;
}

.payment-methods-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
  width: 100%;
}

.payment-method-btn {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: white;
  border-radius: 16px;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 130px;
  min-width: 200px;
  flex: 1 1 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-weight: 800;
  font-size: 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-bottom: 4px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.3);
  text-transform: uppercase;
}

.payment-method-btn:hover:not(.disabled) {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  border-bottom-width: 6px;
  box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.4);
}

.payment-method-btn:active:not(.disabled) {
  transform: translateY(2px);
  border-bottom-width: 1px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.1s;
}

.payment-method-btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  filter: grayscale(1);
}

.method-icon {
  font-size: 2.5rem;
  color: #fbbf24;
  transition: transform 0.3s ease;
}

.payment-name {
  font-size: 1rem;
}

.payment-bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  pointer-events: none;
  z-index: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

/* Processing Screen */
.payment-processing-screen {
  height: 100%;
  width: 100%;
  background: #0f172a;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.processing-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.processing-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.processing-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
}

/* Success Screen */
.payment-success-screen {
  height: 100%;
  width: 100%;
  background: #0f172a;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.success-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
}

.success-icon-wrapper {
  margin-bottom: 1rem;
}

.success-circle {
  width: 6rem;
  height: 6rem;
  background-color: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.success-title {
  font-size: 1.875rem;
  font-weight: 700;
  font-family: serif;
  color: white;
  margin: 0;
}

.success-info-card {
  background: rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 12px;
  backdrop-filter: blur(8px);
  width: 100%;
  max-width: 20rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.success-info-item {
  display: flex;
  flex-direction: column;
}

.success-info-label {
  color: #f59e0b;
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin: 0;
  margin-bottom: 0.25rem;
}

.success-info-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  margin: 0;
}

.success-order-number {
  font-size: 1.25rem;
  color: rgba(209, 213, 219, 1);
  margin: 0;
}

.success-receipt-text {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  margin-top: 1rem;
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
